/**
 * MODULE: domain/pages/frontend/components/editor/PagesTodoBlockView.tsx
 * PURPOSE: Checkbox to-do block in the Pages note editor
 */

import { NodeViewWrapper } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';
import type { ChangeEvent, KeyboardEvent } from 'react';

export function PagesTodoBlockView({ node, selected, updateAttributes, editor }: NodeViewProps) {
  const editable = editor.isEditable;
  const text = String(node.attrs.text ?? '');
  const checked = Boolean(node.attrs.checked);

  const stopKeys = (event: KeyboardEvent) => {
    event.stopPropagation();
  };

  const handleCheck = (event: ChangeEvent<HTMLInputElement>) => {
    updateAttributes({ checked: event.target.checked });
  };

  return (
    <NodeViewWrapper
      className={`my-1 ${selected ? 'ring-2 ring-blue-300 ring-offset-2 dark:ring-blue-700 dark:ring-offset-gray-950' : ''}`}
      data-testid="pages-todo-block"
      data-checked={checked ? 'true' : 'false'}
    >
      <label
        className={`flex items-start gap-3 rounded-md px-1 py-1.5 ${
          checked ? 'opacity-70' : ''
        }`}
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={handleCheck}
          disabled={!editable}
          className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600"
          data-testid="pages-todo-checkbox"
          aria-label={text ? `Mark "${text}" complete` : 'Mark to-do complete'}
        />
        {editable ? (
          <input
            type="text"
            value={text}
            onChange={(event) => updateAttributes({ text: event.target.value })}
            onKeyDown={stopKeys}
            placeholder="To-do"
            className={`min-w-0 flex-1 border-0 bg-transparent text-sm focus:outline-none focus:ring-0 ${
              checked ? 'text-gray-500 line-through dark:text-gray-400' : 'text-gray-900 dark:text-gray-100'
            }`}
            data-testid="pages-todo-text-input"
          />
        ) : (
          <span
            className={`flex-1 text-sm ${
              checked ? 'text-gray-500 line-through dark:text-gray-400' : 'text-gray-900 dark:text-gray-100'
            }`}
            data-testid="pages-todo-text"
          >
            {text || 'To-do'}
          </span>
        )}
      </label>
    </NodeViewWrapper>
  );
}
